<template lang="pug">
    .month-schedule
        calendar(
            ref="calendar"
            color="red"
            :locale="locale"
            :attributes="attributes"
            transition="none"
            disable-page-swipe
            is-expanded
        )
            template(v-slot:day-content="{ day, attributes }")
                .week-number(v-if="isStartOfWeek(day.date)") {{ day.date | formatDate('I') }}
                .day-content.is-flex.is-flex-direction-column
                    .day-label.text-sm.text-gray-900 {{ day.day }}
                    .is-flex-grow-1
                        .slot(
                            v-for="attr in attributes"
                            :style="attr.customData.style"
                            @mouseover="hover(attr.customData.slot)"
                            @mouseleave="blur"
                            @click="showModal(attr.customData.slot)"
                        )
                            h3(v-line-clamp:19.2="1") {{ attr.customData.title }}
                            .small.time(v-line-clamp:15.36="1") {{ attr.customData.time }}
        portal(to="modals")
            FormModal(
                v-if="modal.visible && modal.slot"
                :data="modal.slot"
                @close="hideModal"
                @success="forceReload"
            )
</template>

<script>

import _ from 'lodash'
import Color from 'color'
import { mapGetters, mapMutations } from 'vuex'
import { startOfWeek, startOfMonth, endOfMonth, isSameDay, format, fromUnixTime } from 'date-fns'

import FormModal from './FormModal'
import TeaserList from './TeaserList'

export default {
    name: 'MonthSchedule',
    extends: TeaserList,
    props: {
        current: {
            type: Date,
            default: () => new Date
        },
    },
    data: () => {
        return {
            fetching: false,
            focused: undefined,
            focusTimeout: undefined,
            locale: { id: 'de', masks: { weekdays: 'WW' } },
            modal: {
                visible: false,
                slot: undefined,
            }
        }
    },
    components: {
        FormModal
    },
    watch: {
        current(value) {
            this.load()
            const calendar = this.$refs.calendar
            calendar.move(this.current)
        }
    },
    computed: {
        attributes() {
            let attributes = []
            this.teasers.forEach(teaser => {
                let slot = _.cloneDeep(teaser)
                const event = slot.event

                let hex = event.color || event.category.color

                // check if the slot starts after it ends
                // =====
                // skip slot if true

                if (slot.start_at >= slot.end_at) return true

                let style = {}
                if (hex) {
                    let color = Color(hex)
                    // if (color.isDark()) color = color.lighten(0.4)

                    if (this.focused === slot.id) {
                        if (color.lightness() > 45) color = color.lightness(25)
                        else color = color.lightness(85)
                        style['border-color'] = color.rgb().string()

                        color = Color(hex)
                        const lightness = color.lightness()
                        if (lightness < 93) color = color.lightness(93)

                        // saturation
                        // if (color.color[1] > 20) color.color[1] = 20
                        if (color.color[1] > 30) color.color[1] = 30
                        style['background-color'] = color.alpha(0.6).rgb().string();
                    } else {
                        style['border-color'] = color.rgb().string()

                        color = Color(hex)
                        const lightness = color.lightness()
                        if (lightness < 70) color = color.lightness(70)

                        // saturation
                        // if (color.color[1] > 20) color.color[1] = 20
                        style['background-color'] = color.alpha(0.2).rgb().string();
                    }
                }

                const start = fromUnixTime(slot.start_at)
                const end = fromUnixTime(slot.end_at)

                slot.single_day = isSameDay(start, end)

                let time = `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`
                if (! slot.single_day) {
                    time = `${format(start, 'dd.MM.yy, HH:mm')} - ${format(end, 'dd.MM.yy, HH:mm')}`
                }

                attributes.push({
                    key: slot.id,
                    // dot,
                    dates: { start, end },
                    // popover: {
                    //     label: event.name,
                    //     visibility: 'hover',
                    //     isInteractive: true,
                    // },
                    customData: {
                        title: slot.event.name,
                        time,
                        style,
                        slot
                    }
                })
            })

            return attributes
        },
    },
    methods: {
        isStartOfWeek(date) {
            return isSameDay(startOfWeek(date, { weekStartsOn: 1 }), date)
        },
        load(page = 1, force = false) {
            let pagination = _.get(this.section, 'teasers.pagination')

            // default timespan setting is 'auto'
            let key, timestampFilter;
            if (this.timespan === 'future') key = 'from';
            else if (this.timespan === 'past') key = 'to';
            if (key) {
                timestampFilter = { key }
                timestampFilter[key] = format(new Date, 't')
            }

            const inclusiveFilter = {
                key: 'inclusive_timespan',
                from: format(startOfMonth(this.current), 't'),
                to: format(endOfMonth(this.current), 't'),
            }

            if (! pagination) {
                const categories = this.section.eventCategories.map(category => category.id)
                pagination = {
                    filters: [
                        inclusiveFilter,
                        { key: 'categories', in: categories },
                        { key: 'cms', match: this.cms.id }
                    ],
                    pageInfo: {
                        currentPage: 1,
                        lastPage: 1,
                        total: 0,
                        perPage: 500
                    },
                }
            } else {
                const i = _.findIndex(pagination.filters, { key: 'inclusive_timespan' });

                if (i >= 0) {
                    // skip refetching if the inclusive_timespan did not change
                    if (force === false
                        && pagination.filters[i].from === inclusiveFilter.from
                        && pagination.filters[i].to === inclusiveFilter.to) return

                    pagination.filters[i] = inclusiveFilter
                } else {
                    pagination.filters.push(inclusiveFilter)
                }

                // remove filters related to list display type
                const x = _.findIndex(pagination.filters, { key: 'from' });
                if (x >= 0) pagination.filters.splice(x, 1);
                const y = _.findIndex(pagination.filters, { key: 'to' });
                if (y >= 0) pagination.filters.splice(y, 1);
                // pagination.pageInfo.currentPage = page
            }

            if (timestampFilter) {
                pagination.filters.push(timestampFilter);
            }

            this.fetching = true;

            this.fetchTeasers(pagination)
                .then(({ items, pageInfo }) => {
                    let filters = _.cloneDeep(pagination.filters);
                    const teasers = {
                        loading: false,
                        pagination: { filters, items, pageInfo }
                    };
                    this.insertTeasers({ s: this.s, teasers });
                })
                .finally(() => (this.fetching = false));
        },
        hover(el) {
            if (this.focusTimeout) clearTimeout(this.focusTimeout)
            this.focused = el.id
        },
        blur() {
            this.focusTimeout = setTimeout(() => {
                this.focused = undefined
            }, 150);
        },
        showModal(slot) {
            this.modal.slot = slot
            this.modal.visible = true
        },
        hideModal() {
            this.modal.visible = false
            this.modal.slot = undefined
        },
    },
    beforeDestroy() {
        if (this.modal.visible) {
            this.hideModal()
        }
    },
}
</script>

<style lang="sass" scoped>

@import './resources/variables';
@import "~bulma/sass/utilities/mixins";

$week-bar-width: 55px !default

::-webkit-scrollbar
    width: 0px

::-webkit-scrollbar-track
    display: none

.month-schedule .vc-container
    border-radius: 0
    width: 100%
    position: relative
    overflow: hidden
    border: none
    .week-number
        display: none
    +tablet
        padding-left: $week-bar-width
        &:before
            content: ''
            position: absolute
            left: 0px
            width: $week-bar-width
            height: 100%
            // background: lighten($current-day-background, 3.5%)

        .week-number
            position: absolute
            left: calc((#{$week-bar-width} + 1px) * -1)
            width: $week-bar-width
            text-align: center
            opacity: .2 !important
            font-size: 1rem
            height: 100%
            display: flex
            align-items: center
            justify-content: center

    .day-content
        height: 100%
        font-weight: 400
        .slot
            cursor: pointer
            margin-bottom: 4px
            background: rgba(0, 0, 0, 0.1)
            border-left: 2px solid
            border-radius: $radius
            border-bottom-left-radius: 0
            border-top-left-radius: 0
            // width: calc(100% - 8px)
            padding: 2px 3px
            padding-right: 5px
            line-height: 1.3
            transition: background .15s ease, width .05s ease
            backdrop-filter: blur(2px)
        .small.time
            font-size: .8em

    .day-label
        // text-align: right
        font-weight: normal

    &::v-deep
        .vc-header
            background-color: lighten($current-day-background, 3.5%)
            padding: 10px 0
            display: none

        .vc-arrows-container
            display: none

        .vc-weeks
            padding: 0

        .vc-grid-container.vc-weeks
            overflow: visible !important

        .vc-grid-container.grid
            overflow: visible !important

        .vc-weekday
            // background-color: lighten($current-day-background, 3.5%)
            // border-bottom: 1px solid $border-color
            // border-top: 1px solid $border-color
            white-space: nowrap
            height: 30px
            padding: 8px
            font-weight: normal
            color: rgb(87, 87, 85)
            font-size: 80%
            +tablet
                font-size: 100%
                height: 40px
                padding: 10px

        .vc-day.is-not-in-month *
            opacity: 0.5

        // .darkvc-grid-cell-col-1:not(.vc-grid-cell-row-1)
        //     border-left: 1px solid $border-color
        //
        .vc-day
            padding: 0 5px 3px 5px
            text-align: left
            background-color: white
            font-size: .8rem
            border-bottom: 1px solid $border-color-light
            border-right: 1px solid $border-color-light
            min-height: 30px
            min-width: 30px
            +tablet
                min-height: 111px
                min-width: 90px
            &.is-today
                background-color: $current-day-background

            &.weekday-1,
            &.weekday-7
                // background-color: #eff8ff
                background-color: lighten($current-day-background, 3.5%)

            &.on-top
                border-top: 1px solid $border-color

            &.on-bottom
                border-bottom: 1px solid $border-color

            &.on-left
                border-left: 1px solid $border-color

            &.on-right
                border-right: 1px solid $border-color


        .vc-day-dots
            margin-bottom: 5px


</style>
